import { useId, useState } from 'react'
import { formatCompact, formatValue } from './palette'
import { useT } from '@/i18n'
import { uiMessages } from '@/i18n/messages/ui'

export interface TrendPoint {
  label: string
  value: number
  /** The comparison value at this point (e.g. the company average). */
  reference: number
}

interface Props {
  title: string
  subtitle?: string
  points: TrendPoint[]
  unit: string
  color: string
  /** What the dashed line is, for its legend and the table header. */
  referenceLabel: string
  valueLabel: string
}

/**
 * Columns with a dashed reference line: "this crew, week by week, against everyone".
 *
 * Plain SVG like BarChart, with the same relief: value on hover and a table view. Two series
 * do need a legend, so it gets a small one; the reference is a line, not a second set of bars,
 * so the eye reads it as a benchmark rather than a competitor.
 */
export function TrendChart({ title, subtitle, points, unit, color, referenceLabel, valueLabel }: Props) {
  const t = useT(uiMessages)
  const [hover, setHover] = useState<number | null>(null)
  const [showTable, setShowTable] = useState(false)
  const tableId = useId()

  const W = 600
  const H = 160
  const n = Math.max(points.length, 1)
  const max = Math.max(...points.map((p) => Math.max(p.value, p.reference)), 0) || 1
  const slot = W / n
  const barW = Math.min(slot - 2, 24)
  const y = (v: number) => H - (v / max) * (H - 8)
  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${(i + 0.5) * slot},${y(p.reference)}`).join(' ')
  const h = hover != null ? points[hover] : null

  return (
    <figure className="rounded-2xl bg-white p-3 ring-1 ring-neutral-200 sm:p-5 dark:bg-neutral-900 dark:ring-neutral-800">
      <figcaption className="mb-3 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{title}</h3>
          {subtitle && <p className="mt-0.5 text-xs text-neutral-500">{subtitle}</p>}
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-500">
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: color }} />
              {valueLabel}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <svg aria-hidden width="16" height="4"><line x1="0" y1="2" x2="16" y2="2" stroke="currentColor" strokeWidth="2" strokeDasharray="4 3" /></svg>
              {referenceLabel}
            </span>
          </div>
        </div>
        <button
          onClick={() => setShowTable((v) => !v)}
          aria-expanded={showTable}
          aria-controls={tableId}
          className="shrink-0 rounded-md px-2 py-1 text-xs text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-neutral-100"
        >
          {showTable ? t('viewChart') : t('viewTable')}
        </button>
      </figcaption>

      {showTable ? (
        <table id={tableId} className="w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-200 text-left text-xs text-neutral-500 dark:border-neutral-800">
              <th className="pb-2 font-medium">{t('colLabel')}</th>
              <th className="pb-2 text-right font-medium">{valueLabel}</th>
              <th className="pb-2 text-right font-medium">{referenceLabel}</th>
            </tr>
          </thead>
          <tbody>
            {points.map((p) => (
              <tr key={p.label} className="border-b border-neutral-100 last:border-0 dark:border-neutral-800/60">
                <td className="py-1.5 text-neutral-700 dark:text-neutral-300">{p.label}</td>
                <td className="tabular py-1.5 text-right">{formatValue(p.value, unit)}</td>
                <td className="tabular py-1.5 text-right text-neutral-500">{formatValue(p.reference, unit)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="relative">
          <div className="tabular mb-1 h-4 text-xs text-neutral-600 dark:text-neutral-400">
            {h && <>{h.label}: <strong className="font-semibold">{formatValue(h.value, unit)}</strong> · {formatCompact(h.reference, unit)}</>}
          </div>
          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="block h-40 w-full" onMouseLeave={() => setHover(null)}>
            {[0.25, 0.5, 0.75].map((f) => (
              <line key={f} x1="0" x2={W} y1={H * f} y2={H * f} stroke="currentColor" className="text-neutral-200 dark:text-neutral-800" strokeWidth="1" vectorEffect="non-scaling-stroke" />
            ))}
            {points.map((p, i) => (
              <g key={p.label} onMouseEnter={() => setHover(i)}>
                <rect x={i * slot} y="0" width={slot} height={H} fill="transparent" />
                {p.value > 0 && (
                  <rect
                    x={(i + 0.5) * slot - barW / 2} y={y(p.value)} width={barW} height={H - y(p.value)} rx="3"
                    fill={color} opacity={hover === null || hover === i ? 1 : 0.45}
                  />
                )}
              </g>
            ))}
            <path d={line} fill="none" stroke="currentColor" className="text-neutral-500" strokeWidth="2" strokeDasharray="5 4" vectorEffect="non-scaling-stroke" pointerEvents="none" />
          </svg>
          <div className="mt-1 flex justify-between text-[10px] text-neutral-500">
            <span>{points[0]?.label}</span>
            <span>{points[Math.floor(points.length / 2)]?.label}</span>
            <span>{points[points.length - 1]?.label}</span>
          </div>
        </div>
      )}
    </figure>
  )
}
